import { create } from 'zustand';
import type { CapabilityIdentifier } from '@/features/billing/capability-identifiers';

/**
 * Drives the app-wide upgrade prompt (TRU-543).
 *
 * Two producers open it: a locked feature's own "Upgrade" CTA (which knows the
 * capability the user reached for), and the `MutationCache.onError` backstop in
 * `lib/query-client.ts` for `040005 ResourceAccessDenied` — a gated write that
 * slipped past the UI (a tab left open across a downgrade, a control we haven't
 * gated yet). The backstop has no identifier to hand over, so `identifier` is
 * nullable and the dialog degrades to generic upgrade copy.
 *
 * Ephemeral by design — NOT persisted. A burst of concurrent 040005s all call
 * `open()`, so it must be idempotent: the first identifier wins and a later
 * `open(null)` can't blank it out (an identifier-less backstop firing behind an
 * already-open, identifier-carrying prompt must not downgrade the copy).
 */
export interface UpgradePromptState {
  isOpen: boolean;
  /** The capability the user was denied, or `null` when unknown (the backstop). */
  identifier: CapabilityIdentifier | null;
  open: (identifier?: CapabilityIdentifier | null) => void;
  close: () => void;
}

export const useUpgradePromptStore = create<UpgradePromptState>((set) => ({
  isOpen: false,
  identifier: null,
  open: (identifier = null) =>
    set((state) => ({
      isOpen: true,
      identifier: state.isOpen ? (state.identifier ?? identifier) : identifier,
    })),
  close: () => set({ isOpen: false, identifier: null }),
}));
